import { useLayoutEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import clsx from 'clsx';

import FilterIcon from '~/shared/assets/icons/filter.svg';
import CancelIcon from '~/shared/assets/icons/cancel.svg';
import Typography from '~/shared/components/Typography';

import {
  buildFilterButtonGroups,
  explorationFilterButtonLabel,
  fitFilterButtonGroups,
  hiddenFiltersFromGroups,
  filterButtonRowGapPx,
  type FilterButtonGroup,
  type FilterButtonTag,
} from '../lib/filters/buildFilterButtonGroups';
import { traceExplorationFilterButton } from '../lib/filters/explorationFilterButtonTrace';

import {
  findFilterSlotElement,
  readFilterSlotAvailableWidth,
} from './explorationCanvasChrome';

import type { CapacityBounds } from '~/shared/components/filters/capacityFormat';
import type { ExplorationFilter } from '../types/exploration';

const filterButtonOtherMeasureCounts = [1, 2, 5, 10, 99];

interface ExplorationCanvasFilterButtonProps {
  filters: ExplorationFilter[];
  capacityBounds: CapacityBounds;
  onOpen: () => void;
  onRemoveFilter: (filter: ExplorationFilter) => void;
  onRemoveFilters: (filters: ExplorationFilter[]) => void;
  'aria-expanded'?: boolean;
  'aria-controls'?: string;
}

function otherChipWidthForCount(count: number, measuredWidths: Map<number, number>): number {
  if (count <= 0) {
    return 0;
  }
  if (measuredWidths.has(count)) {
    return measuredWidths.get(count)!;
  }

  const probe =
    filterButtonOtherMeasureCounts.find((candidate) => candidate >= count) ??
    filterButtonOtherMeasureCounts[filterButtonOtherMeasureCounts.length - 1]!;

  return (
    measuredWidths.get(probe) ??
    measuredWidths.get(99) ??
    measuredWidths.get(10) ??
    measuredWidths.get(1) ??
    0
  );
}

export default function ExplorationCanvasFilterButton({
  filters,
  capacityBounds,
  onOpen,
  onRemoveFilter,
  onRemoveFilters,
  'aria-expanded': ariaExpanded,
  'aria-controls': ariaControls,
}: ExplorationCanvasFilterButtonProps) {
  const shellRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const triggerMeasureRef = useRef<HTMLSpanElement>(null);
  const clearAllMeasureRef = useRef<HTMLSpanElement>(null);
  const [fittedGroups, setFittedGroups] = useState<FilterButtonGroup[] | null>(null);
  const [enableTransition, setEnableTransition] = useState(false);
  const hasFilters = filters.length > 0;
  const fullGroups = useMemo(
    () => buildFilterButtonGroups(filters, capacityBounds),
    [filters, capacityBounds]
  );
  const groups = hasFilters ? (fittedGroups ?? []) : [];
  const hiddenFilters = hiddenFiltersFromGroups(filters, groups);
  const primaryFullGroups = useMemo(
    () => fullGroups.filter((group) => group.key !== 'other'),
    [fullGroups]
  );

  useLayoutEffect(() => {
    const shell = shellRef.current;
    const measure = measureRef.current;
    const triggerMeasure = triggerMeasureRef.current;
    const clearAllMeasure = clearAllMeasureRef.current;

    if (!shell || !measure || !triggerMeasure || !clearAllMeasure || !hasFilters) {
      setFittedGroups(null);
      setEnableTransition(false);
      return;
    }

    const updateFittedGroups = () => {
      const slot = findFilterSlotElement(shell);
      const available = readFilterSlotAvailableWidth(shell);
      const triggerWidth =
        triggerMeasure.offsetWidth + filterButtonRowGapPx + clearAllMeasure.offsetWidth;
      const groupPartialWidths: Record<string, number[]> = {};
      const otherChipWidths = new Map<number, number>();

      measure.querySelectorAll<HTMLElement>('[data-partial-width]').forEach((element) => {
        const partialKey = element.dataset.partialWidth;
        if (!partialKey) {
          return;
        }
        const [groupKey, countValue] = partialKey.split(':');
        const count = Number(countValue);
        if (!groupKey || !Number.isFinite(count) || count <= 0) {
          return;
        }
        if (!groupPartialWidths[groupKey]) {
          groupPartialWidths[groupKey] = [];
        }
        groupPartialWidths[groupKey][count - 1] = element.offsetWidth;
      });

      measure.querySelectorAll<HTMLElement>('[data-other-width-count]').forEach((element) => {
        const count = Number(element.dataset.otherWidthCount);
        if (Number.isFinite(count) && count > 0) {
          otherChipWidths.set(count, element.offsetWidth);
        }
      });

      if (available <= 0) {
        traceExplorationFilterButton('fit skipped (no layout width yet)', {
          available,
          triggerWidth,
        });
        setFittedGroups(fullGroups);
        return;
      }

      traceExplorationFilterButton('fit measure', {
        available,
        triggerWidth,
        slotClientWidth: slot?.clientWidth,
      });

      const nextGroups = fitFilterButtonGroups({
        fullGroups,
        filters,
        triggerWidth,
        groupPartialWidths,
        otherChipWidthForCount: (count) => otherChipWidthForCount(count, otherChipWidths),
        availableWidth: available,
      });

      setFittedGroups(nextGroups);
      setEnableTransition(true);
    };

    updateFittedGroups();

    const observer = new ResizeObserver(updateFittedGroups);
    const slot = findFilterSlotElement(shell);
    if (slot) {
      observer.observe(slot);
    } else {
      observer.observe(shell);
    }
    return () => observer.disconnect();
  }, [filters, hasFilters, fullGroups]);

  return (
    <div
      ref={shellRef}
      onClick={onOpen}
      className="relative inline-flex min-w-0 max-w-full cursor-pointer"
    >
      <div
        className={clsx(
          'relative font-medium inline-flex items-center group min-w-0 max-w-full',
          'gap-2 px-2 py-1 h-9 text-sm leading-5 rounded-[8px]',
          'bg-white text-primary-900 border border-gray-300',
          'hover:border-primary-200 active:bg-primary-20 active:border-primary-100',
          'overflow-hidden',
          enableTransition && 'transition-colors duration-300'
        )}
      >
        <button
          type="button"
          aria-expanded={ariaExpanded}
          aria-controls={ariaControls}
          className={clsx(
            'flex items-center gap-1 shrink-0 cursor-pointer',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-300 rounded-sm'
          )}
        >
          <FilterIcon
            className={clsx(
              'size-5 aspect-square shrink-0 group-hover:stroke-primary-400 group-hover:stroke-2',
              hasFilters ? 'stroke-primary-400 stroke-2' : 'stroke-primary-900'
            )}
          />
          <Typography
            variant="p3"
            color="text-primary-900"
            fontWeight={600}
            className="whitespace-nowrap"
          >
            Filters
          </Typography>
        </button>

        {hasFilters && (
          <ClearAllFilterChip
            onClick={(e) => {
              e.stopPropagation();
              onRemoveFilters(filters);
            }}
          />
        )}

        {groups.map((group) => (
          <FilterButtonGroupRow
            key={group.key}
            group={group}
            capacityBounds={capacityBounds}
            hiddenFilters={hiddenFilters}
            onRemoveFilter={onRemoveFilter}
            onRemoveFilters={onRemoveFilters}
          />
        ))}
      </div>

      {hasFilters ? (
        <div
          ref={measureRef}
          aria-hidden
          className="pointer-events-none invisible absolute left-0 top-0 flex h-9 w-max max-w-none items-center gap-2 whitespace-nowrap px-2"
        >
          <span ref={triggerMeasureRef} className="flex shrink-0 items-center gap-1">
            <FilterIcon className="size-5 shrink-0" />
            <span className="whitespace-nowrap text-sm font-semibold leading-5">Filters</span>
          </span>
          <span ref={clearAllMeasureRef} className="inline-flex shrink-0">
            <ClearAllFilterChip />
          </span>
          {primaryFullGroups.flatMap((group) =>
            group.tags.map((_, index) => (
              <span
                key={`${group.key}-${index + 1}`}
                data-partial-width={`${group.key}:${index + 1}`}
                className="inline-flex shrink-0"
              >
                <FilterButtonGroupMeasure
                  group={{ ...group, tags: group.tags.slice(0, index + 1) }}
                />
              </span>
            ))
          )}
          {filterButtonOtherMeasureCounts.map((count) => (
            <span key={count} data-other-width-count={count} className="inline-flex shrink-0">
              <FilterButtonOtherChipMeasure label={`+${count}`} />
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function FilterChipContent({ label, truncate }: { label: string; truncate?: boolean }) {
  return (
    <>
      <span className={clsx(truncate && 'min-w-0 truncate')}>{label}</span>
      <CancelIcon className="size-[13px] shrink-0 text-gray-600" aria-hidden />
    </>
  );
}

const filterChipClassName = clsx(
  'inline-flex h-4 shrink-0 items-center gap-0 rounded bg-primary-50 px-1',
  'text-[10px] font-bold leading-[13px] text-gray-700 whitespace-nowrap'
);

/** "Clear All" is always the first chip and is never folded into width-fit overflow. */
function ClearAllFilterChip({ onClick }: { onClick?: (e: MouseEvent<HTMLButtonElement>) => void }) {
  if (!onClick) {
    return (
      <span className={filterChipClassName}>
        <FilterChipContent label="Clear All" />
      </span>
    );
  }

  return (
    <button
      type="button"
      className={clsx(
        filterChipClassName,
        'cursor-pointer hover:bg-primary-100 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary-300'
      )}
      aria-label="Clear all filters"
      title="Clear all filters"
      onClick={onClick}
    >
      <FilterChipContent label="Clear All" />
    </button>
  );
}

function FilterButtonGroupMeasure({ group }: { group: FilterButtonGroup }) {
  return (
    <span className="flex shrink-0 items-center gap-1">
      <span className="text-[10px] font-medium leading-[13px] text-gray-700 whitespace-nowrap">
        {group.label}
      </span>
      {group.tags.map((tag, index) => (
        <span
          key={`${group.key}-${tag.label}-${index}`}
          className={clsx(filterChipClassName, tag.truncate && 'max-w-[120px]')}
        >
          <FilterChipContent label={tag.label} truncate={tag.truncate} />
        </span>
      ))}
    </span>
  );
}

function FilterButtonOtherChipMeasure({ label }: { label: string }) {
  return (
    <span className="flex shrink-0 items-center gap-1">
      <span className="text-[10px] font-medium leading-[13px] text-gray-700 whitespace-nowrap">
        Other
      </span>
      <span className={filterChipClassName}>
        <FilterChipContent label={label} />
      </span>
    </span>
  );
}

function FilterButtonGroupRow({
  group,
  capacityBounds,
  hiddenFilters,
  onRemoveFilter,
  onRemoveFilters,
}: {
  group: FilterButtonGroup;
  capacityBounds: CapacityBounds;
  hiddenFilters: ExplorationFilter[];
  onRemoveFilter: (filter: ExplorationFilter) => void;
  onRemoveFilters: (filters: ExplorationFilter[]) => void;
}) {
  return (
    <span className="flex items-center gap-1 shrink-0">
      <span className="text-[10px] font-medium leading-[13px] text-gray-700 whitespace-nowrap">
        {group.label}
      </span>
      {group.tags.map((tag, index) => (
        <FilterValueChip
          key={`${group.key}-${tag.label}-${index}`}
          tag={tag}
          groupLabel={group.key === 'other' ? undefined : group.label}
          isOverflow={group.key === 'other'}
          capacityBounds={capacityBounds}
          hiddenFilters={hiddenFilters}
          onRemoveFilter={onRemoveFilter}
          onRemoveFilters={onRemoveFilters}
        />
      ))}
    </span>
  );
}

function FilterValueChip({
  tag,
  groupLabel,
  isOverflow,
  capacityBounds,
  hiddenFilters,
  onRemoveFilter,
  onRemoveFilters,
}: {
  tag: FilterButtonTag;
  groupLabel?: string;
  isOverflow: boolean;
  capacityBounds: CapacityBounds;
  hiddenFilters: ExplorationFilter[];
  onRemoveFilter: (filter: ExplorationFilter) => void;
  onRemoveFilters: (filters: ExplorationFilter[]) => void;
}) {
  const handleRemove = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (isOverflow) {
      onRemoveFilters(hiddenFilters);
    } else {
      onRemoveFilter(tag.filter);
    }
  };

  const overflowTitle = hiddenFilters
    .map((filter) => explorationFilterButtonLabel(filter, capacityBounds))
    .join(', ');

  const ariaLabel = isOverflow
    ? `Remove ${hiddenFilters.length} other filters`
    : groupLabel
      ? `Remove ${tag.label} from ${groupLabel} filters`
      : `Remove ${tag.label} filter`;

  return (
    <button
      type="button"
      className={clsx(
        filterChipClassName,
        'cursor-pointer hover:bg-primary-100 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary-300',
        tag.truncate && 'max-w-[120px]'
      )}
      aria-label={ariaLabel}
      title={isOverflow ? overflowTitle : tag.label}
      onClick={handleRemove}
    >
      <FilterChipContent label={tag.label} truncate={tag.truncate} />
    </button>
  );
}
